import qrcode from "qrcode-generator";
import {} from "../assets/images/icons/rubber-duck.png";
import { ICON } from "constants/icons";
type MakeQRWithLogoOpts = {
  text: string; // QR에 담을 내용(URL 등)
  size?: number; // 최종 픽셀 크기
  logoUrl?: string; // 중앙 로고 URL (동일 도메인 or CORS 허용)
  logoRatio?: number; // QR 너비 대비 로고 너비 비율 (겹치지 않으니 0.22~0.28 권장)
  margin?: number; // 바깥 여백(px)
  dotRadius?: number; // 점의 둥근 정도 (0~0.5; 0=사각, 0.5=완전 원형)
};

export async function makeQrWithSafeLogo({ text, size = 512, logoUrl, logoRatio = 0.24, margin = 24, dotRadius = 0.45 }: MakeQRWithLogoOpts): Promise<string> {
  // 1) QR 데이터 생성 (오류정정은 M/H 아무거나 OK; 겹치지 않게 비우므로 M도 충분)
  const qr = qrcode(0, "H");
  qr.addData(text);
  qr.make();

  const count = qr.getModuleCount();
  const cell = (size - margin * 2) / count;

  // 2) 캔버스(dpr 대응)
  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement("canvas");
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  const ctx = canvas.getContext("2d")!;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.imageSmoothingEnabled = true;

  // 배경
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, size, size);

  // 3) 중앙 로고 세이프존 계산(모듈을 찍지 않음)
  const safeW = size * logoRatio;
  const safeH = safeW; // 정사각형 권장
  const safeX = (size - safeW) / 2;
  const safeY = (size - safeH) / 2;

  const inSafeZone = (x: number, y: number, w: number, h: number) => !(x + w <= safeX || x >= safeX + safeW || y + h <= safeY || y >= safeY + safeH);

  // 4) 둥근 점으로 모듈 찍기 (세이프존은 건너뜀)
  const drawRoundRect = (x: number, y: number, w: number, h: number, r: number) => {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
    ctx.fill();
  };

  // finder(모서리 세 개) 영역은 원형이 아닌 라운드 사각형으로 더 두껍게
  const isFinder = (r: number, c: number) => {
    const n = 7; // finder 크기(7x7)
    const at = (rr: number, cc: number) => r >= rr && r < rr + n && c >= cc && c < cc + n;
    return at(0, 0) || at(0, count - n) || at(count - n, 0);
  };

  ctx.fillStyle = "#000";
  const radiusPx = cell * dotRadius;

  for (let r = 0; r < count; r++) {
    for (let c = 0; c < count; c++) {
      if (!qr.isDark(r, c)) continue;

      const x = Math.round(margin + c * cell);
      const y = Math.round(margin + r * cell);
      const w = Math.ceil(cell);
      const h = Math.ceil(cell);

      // 세이프존이면 그리지 않음 → 중앙이 완전히 비게 됨
      if (inSafeZone(x, y, w, h)) continue;

      if (isFinder(r, c)) {
        // finder는 라운드 사각형 느낌으로 조금 더 각지게
        drawRoundRect(x, y, w, h, Math.min(radiusPx, cell * 0.2));
      } else {
        // 일반 모듈은 둥글게
        drawRoundRect(x, y, w, h, radiusPx);
      }
    }
  }

  // 5) 세이프존 안은 흰색 둥근 사각형으로 정리(모서리 부드럽게)
  ctx.fillStyle = "#ffffff";
  drawRoundRect(safeX, safeY, safeW, safeH, Math.min(safeW, safeH) * 0.2);

  // 6) 로고 그리기(겹치지 않음: 이미 비워놓았으니까)
  if (logoUrl) {
    const logo = await new Promise<HTMLImageElement>((res, rej) => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // 외부 이미지면 CORS 필요
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = logoUrl;
    });

    // 세이프존 안에 맞춰 contain
    const scale = Math.min(safeW / logo.width, safeH / logo.height) * 0.9; // 살짝 여백
    const lw = Math.round(logo.width * scale);
    const lh = Math.round(logo.height * scale);
    const lx = Math.round(safeX + (safeW - lw) / 2);
    const ly = Math.round(safeY + (safeH - lh) / 2);
    ctx.drawImage(logo, lx, ly, lw, lh);
  }

  // 결과 PNG dataURL 반환
  return canvas.toDataURL("image/png");
}
